
import { mountThemeFeature } from './editor-lifecycle.js';

mountThemeFeature(lifecycle => {
const { IntersectionObserver, ResizeObserver, setTimeout, setInterval, requestAnimationFrame } = lifecycle;
/* ============================================================
   SHOP SCENE — the still life that knows the time.
   Four match-cut frames of one photograph (first light, morning,
   midday, dusk), keyed by product: hovering a jar crossfades its
   hour in and surfaces a card. Where hover doesn't exist (touch,
   portrait), scroll plays the sun: the day advances and the
   camera pans the shelf, one product moment at a time.
   No libraries; opacity + one translateX, all rAF-throttled.
   ============================================================ */

const $  = (s, c = document) => c.querySelector(s);
const $$ = (s, c = document) => [...c.querySelectorAll(s)];

const scene  = $('[data-scene]');
if (scene) {
  const stage  = $('[data-stage]', scene);
  const canvas = $('[data-canvas]', scene);
  const copy   = $('[data-scene-copy]', scene);
  const frames = Object.fromEntries($$('.scene-frame', scene).map(f => [f.dataset.frame, f]));
  const cards  = Object.fromEntries($$('.scene-card', scene).map(c => [c.dataset.card, c]));
  const spots  = Object.fromEntries($$('.scene-spot', scene).map(s => [s.dataset.spot, s]));
  const KEYS   = ['citrus', 'deep', 'ageless'];          /* frame stack order = day order */
  const AR     = 16 / 9;

  const reduced   = matchMedia('(prefers-reduced-motion: reduce)');
  const hoverable = matchMedia('(hover: hover) and (pointer: fine)');
  const portrait  = matchMedia('(orientation: portrait)');   /* S19: the journey is a portrait composition — landscape gets touch mode */

  /* the day's stops: scroll progress → which hour, and where the camera looks.
     x = each jar's centre as a fraction of the photograph's width. */
  const STOPS = [
    { p: 0.00, x: 0.400, id: null      },   /* intro frames the shelf just left of centre so the title gets clear stone */
    { p: 0.30, x: 0.262, id: 'citrus'  },
    { p: 0.62, x: 0.500, id: 'deep'    },
    { p: 0.92, x: 0.765, id: 'ageless' },
  ];
  const TALL = { r: 1.7917, top: 0.5007, h: 0.3114, dx: 0.0104 };   /* S18: portrait plate geometry, cross-correlated so its band lands under the true frames */
  const tallEl = $('[data-tall]', scene);
  const smooth = t => t * t * (3 - 2 * t);
  const clamp  = (v, a, b) => Math.min(b, Math.max(a, v));

  let mode = null, canvasW = 0, framesLoaded = false, raf = 0;
  let panX = 0;   /* touch mode: the finger pans the photograph */

  /* ---------- lazy hour-frames (the base frame loads eagerly) ---------- */
  function loadFrames() {
    if (framesLoaded) return; framesLoaded = true;
    $$('.scene-frame[data-src]', scene).forEach(f => {
      f.srcset = f.dataset.srcset; f.src = f.dataset.src;
      f.removeAttribute('data-src'); f.removeAttribute('data-srcset');
    });
  }
  if (document.readyState === 'complete') setTimeout(loadFrames, 250);
  else lifecycle.listen(window, 'load', () => setTimeout(loadFrames, 250), { once: true });

  /* ---------- layout: size the canvas as the photograph's cover box ---------- */
  function layout() {
    /* the stage is always one viewport tall; clamp the measurement so a stale
       or mid-restore reading can never oversize (zoom) the canvas */
    const vw = stage.clientWidth || innerWidth;
    const vh = Math.min(stage.clientHeight || innerHeight, innerHeight);
    let cw, ch;
    if (mode === 'hover') { cw = Math.max(vw, vh * AR); ch = Math.max(vh, vw / AR); }
    else { ch = vh; cw = ch * AR; if (cw < vw) { cw = vw; ch = cw / AR; } }
    canvasW = cw;
    canvas.style.width = cw + 'px';
    canvas.style.height = ch + 'px';
    canvas.style.top = (vh - ch) / 2 + 'px';
    if (tallEl) {
      const tH = ch / TALL.h;
      tallEl.style.width = cw + 'px';
      tallEl.style.height = tH + 'px';
      tallEl.style.top = -(TALL.top * tH) + 'px';
      tallEl.style.left = (TALL.dx * cw) + 'px';
    }
    if (mode === 'hover') canvas.style.transform = `translateX(${(vw - cw) / 2}px)`;
    /* cards sit on the stage (the canvas pans); in hover mode, pin each to
       its jar's image coordinates */
    Object.values(cards).forEach(c => {
      if (mode === 'hover') {
        const sx = parseFloat(getComputedStyle(c).getPropertyValue('--sx')) / 100;
        const sy = parseFloat(getComputedStyle(c).getPropertyValue('--sy')) / 100;
        c.style.left = (vw - cw) / 2 + sx * cw + 'px';
        c.style.top = (vh - ch) / 2 + sy * ch + 'px';
      } else { c.style.left = ''; c.style.top = ''; }
    });
  }

  /* ---------- shared state ---------- */
  function setCards(id) {
    KEYS.forEach(k => {
      cards[k].classList.toggle('is-on', k === id);
      spots[k]?.setAttribute('aria-expanded', String(k === id));
      spots[k]?.classList.toggle('is-hot', k === id);
    });
  }

  /* ================= HOVER MODE — touch a jar, its hour arrives ================= */
  let hot = null, tIn = 0, tOut = 0;
  function activate(id) {
    clearTimeout(tOut);
    if (hot === id) return;
    loadFrames();
    hot = id; scene.dataset.state = id;
    KEYS.forEach(k => { frames[k].style.opacity = k === id ? 1 : 0; });
    setCards(id);
  }
  function deactivate() {
    if (!hot) return;
    hot = null; delete scene.dataset.state;
    KEYS.forEach(k => { frames[k].style.opacity = 0; });
    setCards(null);
  }
  Object.entries(spots).forEach(([id, s]) => {
    lifecycle.listen(s, 'pointerenter', () => { if (mode !== 'hover') return; clearTimeout(tIn); tIn = setTimeout(() => activate(id), 120); });
    lifecycle.listen(s, 'pointerleave', () => { if (mode !== 'hover') return; clearTimeout(tIn); clearTimeout(tOut); tOut = setTimeout(deactivate, 240); });
    lifecycle.listen(s, 'click', () => { if (mode !== 'hover' && mode !== 'touch') return; hot === id ? deactivate() : activate(id); });
    lifecycle.listen(s, 'focus', () => { if (mode === 'hover') activate(id); });
  });
  Object.values(cards).forEach(c => {
    lifecycle.listen(c, 'pointerenter', () => { if (mode === 'hover') { clearTimeout(tOut); } });
    lifecycle.listen(c, 'pointerleave', () => { if (mode !== 'hover') return; clearTimeout(tOut); tOut = setTimeout(deactivate, 240); });
  });
  lifecycle.listen(scene, 'focusout', e => {
    if (mode !== 'hover') return;
    if (!scene.contains(e.relatedTarget)) deactivate();
  });
  lifecycle.listen(document, 'keydown', e => { if (e.key === 'Escape' && (mode === 'hover' || mode === 'touch')) deactivate(); });

  /* ================= TOUCH MODE — the finger pans the photograph ================= */
  let dragX0 = null, dragPan0 = 0, dragged = false;
  lifecycle.listen(stage, 'pointerdown', e => {
    if (mode !== 'touch') return;
    dragX0 = e.clientX; dragPan0 = panX; dragged = false;
    try { stage.setPointerCapture(e.pointerId); } catch (err) { /* older engines */ }
  });
  lifecycle.listen(stage, 'dragstart', e => { if (mode === 'touch') e.preventDefault(); });
  lifecycle.listen(stage, 'pointermove', e => {
    if (mode !== 'touch' || dragX0 === null) return;
    const dx = e.clientX - dragX0;
    if (Math.abs(dx) > 6) dragged = true;
    const vw = stage.clientWidth || innerWidth;
    panX = clamp(dragPan0 + dx, vw - canvasW, 0);
    canvas.style.transform = `translateX(${panX}px)`;
  });
  lifecycle.listen(stage, 'pointerup', () => { dragX0 = null; });
  lifecycle.listen(stage, 'pointercancel', () => { dragX0 = null; });
  lifecycle.listen(stage, 'click', e => {
    if (mode !== 'touch' || dragged) return;
    if (!e.target.closest('.scene-spot') && !e.target.closest('.scene-card')) deactivate();
  });

  /* ================= JOURNEY MODE — scroll is the sun (S17: with a
     zoom overture: arrive on the whole shelf, dive into the first jar,
     then travel the day) ================= */
  const ZOOM_HOLD = 0.06, ZOOM_END = 0.22;
  const ZSTOPS = [
    { p: ZOOM_END, x: 0.262, id: 'citrus'  },
    { p: 0.58,     x: 0.500, id: 'deep'    },
    { p: 0.90,     x: 0.765, id: 'ageless' },
  ];
  function journeyUpdate() {
    const total = scene.offsetHeight - stage.offsetHeight;
    const p = clamp(-scene.getBoundingClientRect().top / Math.max(1, total), 0, 1);
    if (p > 0.02) loadFrames();

    const vw = stage.clientWidth, vh = stage.clientHeight;

    /* scale: the portrait plate covers the screen, then the camera dives */
    const chv = canvasW / AR;
    const tH = chv / TALL.h;
    const s0 = Math.min(1, Math.max(vw / canvasW, vh / tH));
    let zt;
    if (p < ZOOM_HOLD) zt = 0;
    else if (p < ZOOM_END) zt = smooth((p - ZOOM_HOLD) / (ZOOM_END - ZOOM_HOLD));
    else zt = 1;
    const sc = s0 + (1 - s0) * zt;

    /* the camera's gaze: shelf centre during the overture, then jar to jar */
    let cx;
    if (p < ZOOM_HOLD) cx = 0.5;
    else if (p < ZOOM_END) cx = 0.5 + (ZSTOPS[0].x - 0.5) * smooth((p - ZOOM_HOLD) / (ZOOM_END - ZOOM_HOLD));
    else {
      cx = ZSTOPS[ZSTOPS.length - 1].x;
      for (let i = 0; i < ZSTOPS.length - 1; i++) {
        const A = ZSTOPS[i], B = ZSTOPS[i + 1];
        if (p <= B.p) { cx = A.x + (B.x - A.x) * smooth((p - A.p) / (B.p - A.p)); break; }
      }
    }

    const cw = canvasW * sc, ch = chv * sc;
    const tx = cw < vw ? (vw - cw) / 2 : clamp(vw / 2 - cx * cw, vw - cw, 0);
    /* vertical gaze: the plate's centre during the overture, the band when zoomed */
    const tallTopOff = -(TALL.top * tH);
    const yFocus = (tallTopOff + tH / 2) * (1 - zt) + (chv / 2) * zt;
    const ty = clamp(vh / 2 - sc * yFocus, vh - sc * (tallTopOff + tH), -sc * tallTopOff);
    canvas.style.transform = `translate(${tx}px, ${ty}px) scale(${sc})`;

    /* the true photograph fades in over the plate only once the dive has
       carried the plate's junctions off screen (S18b: the paste seam was a
       geometric mismatch no feather could hide) */
    const base = $('.scene-frame.is-base', scene);
    if (base) base.style.opacity = smooth(clamp((zt - 0.88) / 0.12, 0, 1));

    /* the day: each hour-frame fades in as its jar takes the frame */
    KEYS.forEach((k, i) => {
      const from = i === 0 ? ZOOM_HOLD + 0.04 : ZSTOPS[i - 1].p;
      const t = clamp((p - from) / (ZSTOPS[i].p - from), 0, 1);
      frames[k].style.opacity = smooth(t);
    });

    /* the moment's card, docked at the foot of the stage */
    let active = null;
    ZSTOPS.forEach(z => { if (Math.abs(p - z.p) < 0.11) active = z.id; });
    setCards(active);

    /* the opening words leave as the dive begins */
    copy.style.opacity = p < ZOOM_HOLD ? 1 : clamp(1 - (p - ZOOM_HOLD) / 0.08, 0, 1);
  }
  function onScroll() {
    if (mode !== 'journey' || raf) return;
    raf = requestAnimationFrame(() => { raf = 0; journeyUpdate(); });
  }
  lifecycle.listen(window, 'scroll', onScroll, { passive: true });

  /* ================= MODE ================= */
  function applyMode() {
    const next = hoverable.matches ? 'hover' : ((reduced.matches || !portrait.matches) ? 'touch' : 'journey');   /* S17: scroll journey with a zoom overture; touch mode serves reduced motion and landscape phones — the journey's plate only composes upright */
    if (next !== mode) {
      mode = next;
      scene.classList.toggle('scene--journey', mode === 'journey');
      scene.classList.toggle('scene--hover', mode === 'hover');
      scene.classList.toggle('scene--touch', mode === 'touch');
      deactivate();
      copy.style.opacity = '';
      if (mode === 'hover' || mode === 'touch') { KEYS.forEach(k => { frames[k].style.opacity = 0; }); setCards(null); }   /* a rotation mid-journey leaves hour frames inline-lit */
      const baseF = $('.scene-frame.is-base', scene);
      if (baseF) baseF.style.opacity = '';
    }
    layout();
    if (mode === 'touch') {
      loadFrames();
      const vw = stage.clientWidth || innerWidth;
      panX = clamp(vw / 2 - 0.495 * canvasW, vw - canvasW, 0);
      canvas.style.transform = `translateX(${panX}px)`;
    }
    if (mode === 'journey') journeyUpdate();
  }
  let rT = 0;
  lifecycle.listen(window, 'resize', () => { clearTimeout(rT); rT = setTimeout(applyMode, 120); });
  lifecycle.listen(hoverable, 'change', applyMode);
  lifecycle.listen(reduced, 'change', applyMode);
  lifecycle.listen(portrait, 'change', applyMode);
  applyMode();
  /* back/forward-cache restores resume the page as it was frozen — if the
     window changed size in between, no resize event arrives, and the scene
     comes back zoomed. pageshow fires on every arrival, so re-derive there. */
  lifecycle.listen(window, 'pageshow', applyMode);
}

});
